'use client';

import { Anchor, Chip } from '@components/common';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { voicePhoneDisplay, voicePhoneTelHref } from '@utils/contactPhone';

/**
 * Dial intent from the navbar, named like its neighbours in the add-job menu
 * (`nav_add_job_click`, `nav_add_task_click`) so the three read as one funnel.
 *
 * `surface` is constant today — one component serves both breakpoints — and is sent
 * anyway to keep the parameter shape identical to those events.
 */
const trackContactPhoneClick = () => {
  sendGoogleTagEvent('nav_contact_phone_click', 'Custom', { surface: 'navbar' });
};

/**
 * @description The voice contact number in the navbar, beside the filters icon. Tapping it dials — the
 * handset places the call, we do not. Not the WhatsApp line: in production the two are separate numbers.
 *
 * Renders nothing when no number is configured, rather than a chip with a bare `+` in it.
 *
 * `Anchor`, not `LinkStyled`: the latter runs the href through the i18n path helper, which prefixes
 * the active locale to anything it does not recognise as a translated route — turning `tel:+40…`
 * into `/entel:+40…` on English pages. Every other phone CTA in the app uses a plain anchor for the
 * same reason.
 */
export const ContactPhoneChip = () => {
  const href = voicePhoneTelHref();
  if (!href) return null;

  return (
    <Anchor
      styling="none"
      href={href}
      // Fire-and-forget: the event must not delay or block the handset taking over.
      onClick={trackContactPhoneClick}
    >
      {/* nowrap: the grouped number must not break across lines in the tight mobile row. */}
      <Chip className="whitespace-nowrap border-surface-100 bg-surface-100">
        {voicePhoneDisplay()}
      </Chip>
    </Anchor>
  );
};
